import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@bfc-software/pallet";
import type { TFunction } from "i18next";

import type { TaskRow } from "../-data/task-model";
import type { Task } from "../-data/types";

import {
  NotReleasedMark,
  Progress,
  TaskIdentity,
  TaskStatusTag,
} from "./task-bits";
import styles from "./shared.module.css";

/**
 * The task table, used by both tabs.
 *
 * One component with an optional plan column rather than two tables: the columns
 * either side of it are identical, and the version that diverges is the one where
 * a fix to the progress cell lands in the All tasks table and not in the plan's.
 *
 * Also one component across the two task worlds, now that the plans tab can
 * expand a card onto a plan's unactivated tasks (§0): the Selector, Progress and
 * Status cells each switch on `row.task.kind`/`row.progress.kind` rather than
 * the table splitting into an activated version and a planned one, for the same
 * reason the plan column is optional rather than a second table - the version
 * that diverges is the one where a fix lands in one world and not the other.
 */
export function TaskTable({
  rows,
  ariaLabel,
  showPlan = false,
  onSelect,
  t,
}: {
  rows: TaskRow[];
  ariaLabel: string;
  /** The All tasks tab names each task's plan; inside a card it is redundant. */
  showPlan?: boolean;
  /**
   * The whole row's task, not its id.
   *
   * The detail drawer is addressed by plan *and* task number, and only one of
   * those is the id a row was clicked by - handing back the id alone would make
   * every caller look the task up again in the list it had just handed us.
   */
  onSelect: (task: Task) => void;
  t: TFunction;
}) {
  return (
    <Table aria-label={ariaLabel} isResizable={false}>
      <TableHead>
        <TableHeaderCell isRowHeader={true}>
          {t("tasks.table.taskRoute")}
        </TableHeaderCell>
        {showPlan ? (
          <TableHeaderCell>{t("tasks.table.plan")}</TableHeaderCell>
        ) : (
          <></>
        )}
        <TableHeaderCell>{t("tasks.table.selector")}</TableHeaderCell>
        <TableHeaderCell align="right">
          {t("tasks.table.lines")}
        </TableHeaderCell>
        <TableHeaderCell>{t("tasks.table.progress")}</TableHeaderCell>
        <TableHeaderCell>{t("tasks.table.status")}</TableHeaderCell>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={row.task.id}
            data-test-role="task-row"
            onAction={() => onSelect(row.task)}
          >
            <TableCell>
              <TaskIdentity
                taskId={row.task.id}
                routeCodes={row.task.routeCodes}
                t={t}
              />
            </TableCell>
            {showPlan ? (
              <TableCell>
                {/* The plan's warehouse-scoped number, not the ULID the row is
                    keyed by - a manager reads out "PLAN-3120". */}
                <span className={styles.planId}>{row.task.planName}</span>
              </TableCell>
            ) : (
              <></>
            )}
            <TableCell>
              {/*
                A task nobody has claimed says so rather than showing a dash: the
                empty cell reads as missing data, and this is the column a manager
                scans to find work to hand out. A planned task has no selector
                field at all - it is held by nobody, not by an unnamed somebody -
                so it renders the shared em-dash rather than "Unassigned", which
                would claim the task is sitting in a pool it cannot be in yet.
              */}
              <span className={styles.muted}>
                {row.task.kind === "activated"
                  ? (row.task.selectorName ?? t("tasks.row.unassigned"))
                  : t("tasks.row.none")}
              </span>
            </TableCell>
            <TableCell align="right">
              <span className={styles.muted}>{row.task.lineCount}</span>
            </TableCell>
            <TableCell>
              {row.progress.kind === "known" ? (
                <Progress
                  done={row.progress.picksDone}
                  total={row.progress.picksTotal}
                  percent={row.progress.percent}
                />
              ) : (
                <span className={styles.muted}>{t("tasks.row.none")}</span>
              )}
            </TableCell>
            <TableCell>
              {row.task.kind === "activated" ? (
                <TaskStatusTag status={row.task.status} t={t} />
              ) : (
                <NotReleasedMark t={t} />
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
